/**
 * 기업 설정 — AI 보고서
 * ---------------------------------------------------------------------------
 * 기업이 고르는 것은 **어느 엔진을 쓸지** 하나뿐이다. API 키도, 모델도,
 * 원가도 공급사가 쥔다(시스템 > AI 설정). 기업이 보는 것은 "1건에 얼마" 다.
 *
 * ## 「쓸지 말지」는 고르지 않는다
 * 예전에는 기업이 AI 사용 여부를 고르게 했다. 고르지 않은 기업은 화면에서
 * 단추가 막힌 이유를 모른 채 문의했고, 켜 두어도 **쓰지 않으면 0원**이라
 * 고르게 할 이유가 없었다. 지금은 세 제품 요금제에 기본으로 들어 있고,
 * 만든 건수만 청구서에 줄이 선다.
 *
 * ## 왜 여기 있나
 * 설정 정의(config.setting_definition)에 넣어 두면 화면이 알아서 그려 줄
 * 거라 생각했는데, 이 화면은 **고정 탭**으로 되어 있어 그 표를 읽지 않는다.
 * 그래서 탭을 하나 더한다 — 기업이 "어디서 쓸 수 있나, 얼마인가, 무엇으로
 * 돌아가나" 를 한자리에서 보는 곳이다.
 */

import { useEffect, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface AiConfig {
  enabled: boolean;
  provider: string;
  providerName: string;
  model: string | null;
  pricePerReport: number | null;
  maxInputChars: number;
  disabledReason: string | null;
  /** 이번 달 무료 건수 — 기업 단위(사람 수와 무관), 이월되지 않는다 */
  freePerMonth: number;
  /** 이번 달 이미 만든 건수 */
  usedThisMonth: number;
  /** 남은 무료 건수. 0 이면 다음 1건부터 돈이 붙는다 */
  freeRemaining: number;
  /** 이번 달의 시작일. 「2026년 10월」처럼 적는다 */
  periodStart: string;
}

/** 「2026-10-01」 → 「2026년 10월」 */
function monthLabel(iso: string | undefined): string {
  if (!iso) return '이번 달';
  const [y, m] = iso.split('-');
  return y && m ? `${y}년 ${Number(m)}월` : '이번 달';
}

/**
 * 제품마다 단가가 따로 걸린다(hub.ai_report · crm.ai_report · eco.ai_report).
 * 지금은 셋 다 같은 금액이지만, 한 제품만 올리거나 내릴 수 있어야 한다.
 */
const PRODUCTS = [
  { code: 'hub', name: 'WORK&Hub', where: '기업 현황 화면의 [AI 주간 보고서]' },
  { code: 'crm', name: 'WORK&CRM', where: '영업활동 보고서 · 프로젝트 보고서' },
  { code: 'eco', name: 'WORK&ECO', where: '대시보드의 [AI 주간 보고서]' },
] as const;

export default function AiReportPanel({ canManage }: { canManage: boolean }) {
  const client = useQueryClient();
  const { message } = AntdApp.useApp();
  const [choice, setChoice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const configs = useQueries({
    queries: PRODUCTS.map((p) => ({
      queryKey: ['tenant', 'ai-config', p.code],
      queryFn: async () => (await apiClient.get<AiConfig>(`/products/${p.code}/ai-config`)).data,
    })),
  });

  const loading = configs.some((q) => q.isLoading);
  const failed = configs.find((q) => q.isError);
  // 엔진은 기업 설정 하나다. 어느 제품 응답에서 읽어도 같다.
  const any = configs.find((q) => q.data)?.data;
  const usable = configs.filter((q) => q.data?.enabled).length;

  useEffect(() => {
    if (any) setChoice(any.provider);
  }, [any]);

  const save = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      /*
       * 기업 설정 저장은 공통 설정 경로를 쓴다. 값은 JSON 이라 문자열도
       * 따옴표를 포함한 형태로 보낸다 — 서버가 jsonb 로 받는다.
       */
      await apiClient.put('/config/settings/ai.provider', {
        value: choice,
        reason: 'AI 보고서 엔진 변경',
      });
      message.success('AI 엔진을 바꿨습니다. 다음 보고서부터 적용됩니다.');
      await client.invalidateQueries({ queryKey: ['tenant', 'ai-config'] });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setSaving(false);
    }
  };

  const dirty = Boolean(any && choice && choice !== any.provider);

  const rows = PRODUCTS.map((p, i) => ({
    key: p.code,
    name: p.name,
    where: p.where,
    config: configs[i]?.data,
  }));

  return (
    <Card loading={loading}>
      {failed?.error && (
        <Alert type="error" showIcon message={errorText(failed.error as unknown as NormalizedError)} />
      )}

      {!loading && usable > 0 && (
        <Alert
          type="success"
          showIcon
          icon={<ThunderboltOutlined />}
          style={{ marginBottom: 20 }}
          message={`AI 보고서는 요금제에 기본으로 들어 있습니다 — 제품마다 ${monthLabel(any?.periodStart)}에 쓸 수 있는 무료 건수가 있습니다`}
          description={
            <>
              따로 신청하실 것이 없습니다. <b>무료 건수 안에서는 0원</b>이고, 그보다 더 만든
              건수만 다음 청구서에 더해집니다. 무료 건수는 <b>기업 단위</b>(사람 수와 무관)이며{' '}
              <b>매월 1일에 새로 시작하고 남은 것은 이월되지 않습니다.</b> 만들다 실패하면
              건수에서 빼지 않습니다.
            </>
          }
        />
      )}

      <Table
        size="small"
        bordered
        pagination={false}
        style={{ marginBottom: 20 }}
        dataSource={rows}
        columns={[
          { title: '제품', dataIndex: 'name', width: 130 },
          {
            /*
              무료 건수를 **단가보다 왼쪽에** 둔다. 먼저 읽히는 자리에 값이
              있어야 "이게 처음부터 돈 드는 기능인가" 라는 오해가 생기지 않는다.
            */
            title: '무료 제공',
            width: 120,
            render: (_, row) =>
              row.config?.enabled ? (
                <Text strong>{row.config.freePerMonth.toLocaleString('ko-KR')}건 / 월</Text>
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
          {
            title: '이번 달 사용',
            width: 150,
            render: (_, row) => {
              const c = row.config;
              if (!c?.enabled) return <Text type="secondary">—</Text>;
              const over = c.usedThisMonth > c.freePerMonth;
              return (
                <Space size={6}>
                  <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {c.usedThisMonth.toLocaleString('ko-KR')}건
                  </Text>
                  {over ? (
                    <Tag color="orange">
                      무료 소진 · 초과 {(c.usedThisMonth - c.freePerMonth).toLocaleString('ko-KR')}건
                    </Tag>
                  ) : (
                    <Tag color="green">무료 {c.freeRemaining.toLocaleString('ko-KR')}건 남음</Tag>
                  )}
                </Space>
              );
            },
          },
          {
            title: '무료 초과 시 1건',
            width: 150,
            render: (_, row) =>
              row.config?.enabled ? (
                <>
                  <Text strong>{(row.config.pricePerReport ?? 0).toLocaleString('ko-KR')}원</Text>
                  <Text type="secondary"> (VAT 별도)</Text>
                </>
              ) : (
                <Tag>미포함</Tag>
              ),
          },
          {
            title: '만드는 곳',
            render: (_, row) =>
              row.config?.enabled ? (
                <Text type="secondary">{row.where}</Text>
              ) : (
                <Text type="secondary">{row.config?.disabledReason ?? '—'}</Text>
              ),
          },
        ]}
      />

      <Descriptions bordered size="small" column={2} style={{ marginBottom: 20 }}>
        <Descriptions.Item label="지금 쓰는 엔진">{any?.providerName ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="한 번에 보낼 수 있는 분량">
          {(any?.maxInputChars ?? 0).toLocaleString('ko-KR')}자
        </Descriptions.Item>
        <Descriptions.Item label="한 번에 분석하는 기간" span={2}>
          최대 1개월
          <Text type="secondary"> — 자료가 많을수록 요약이 뭉개지고 비용도 늘어납니다</Text>
        </Descriptions.Item>
      </Descriptions>

      <Paragraph style={{ marginBottom: 8 }}>
        <Text strong>AI 엔진</Text>
      </Paragraph>
      <Radio.Group
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        disabled={!canManage || usable === 0}
      >
        <Space direction="vertical">
          <Radio value="claude">
            Claude <Text type="secondary">— 기본값. 요약·분석이 또렷합니다</Text>
          </Radio>
          <Radio value="gemini">
            Gemini <Text type="secondary">— 긴 자료를 다룰 때 유리할 수 있습니다</Text>
          </Radio>
        </Space>
      </Radio.Group>

      <div style={{ marginTop: 20 }}>
        <Button type="primary" loading={saving} disabled={!dirty} onClick={() => void save()}>
          저장
        </Button>
      </div>

      <Paragraph type="secondary" style={{ marginTop: 20, marginBottom: 0, fontSize: 12 }}>
        어느 엔진을 쓰든 <b>무료 건수와 요금은 같습니다</b> — 보고서 1건 기준입니다. 엔진은 세
        제품에 함께 적용됩니다. 무료 건수는 제품마다 따로 셉니다(WORK&Hub 의 무료분을 WORK&CRM
        에서 당겨 쓸 수 없습니다). API 키와 모델은 서비스 공급사가 관리하므로 따로 준비하실 것이
        없습니다. 만들다 실패하면 청구하지 않습니다.
      </Paragraph>
    </Card>
  );
}
